import { MapStationInfo } from '@openlayers/MapStationInfo';
import { mapUI, mapWrapper } from '@pages/map/map';
import { modelsPanelStore } from '@openlayers/models/ui/ModelsPanel';
import { ModelsSurfaceHeightAtLocation } from '@shared/map/ModelsSurfaceHeightAtLocation';
import { Events } from '@shared/Events';
import { LStorage } from '@shared/Storage';
import { CollapsiblePanel } from '@shared/ui-react/CollapsiblePanel';
import { autorun, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { transform as olTransform } from 'ol/proj';
import { ModelsSurfaceProps } from '@shared/map/ModelsSurfaceProps';
import * as React from 'react';
import './StationAndModelInfoBoxDesktop.scss';
import { FloatingWindowSimpleComponent } from '@shared/ui-react/FloatingWindowSimpleComponent';

@observer
export class StationAndModelInfoBoxDesktop extends React.Component {
    @observable stationInfo: StationCoords = null;
    @observable modelIds = [];
    @observable lat = 0;
    @observable lon = 0;
    @observable modelInfo: IModelPropsInfo = {
        id: null,
        name: null,
        props: null,
        isStructure: false,
    };

    @observable filter = '';
    @observable collapsed = false;
    @observable hidden = false;
    @observable showHeights = true;
    @observable showProperties = true;
    @observable showAlignment = true;
    @observable showMinimizeOverlay = false;
    @observable psetsCollapsibleMap = new Map();

    constructor(props) {
        super(props);
        makeObservable(this);

        Events.on('mapStationAdded', (e, data) => (this.stationInfo = data.measure));
        Events.on('mapStationRemoved', () => (this.stationInfo = null));

        this.loadSettings();
        autorun(this.persistSettings);
    }

    componentDidMount() {
        Events.on('mapClickMarker:update', this.onUpdateMarker);
        Events.on('mapClickModel', this.onUpdateModel);
        mapWrapper.featureLayers.models.layerSource.on('addfeature', this.onUpdateMarker);
        mapWrapper.featureLayers.models.layerSource.on('removefeature', this.onUpdateMarker); // never fires because of perf optimization, see LayerWrapper
        this.onUpdateMarker();
    }

    persistSettings = () => {
        LStorage.setItem('map__station-box', {
            collapsed: this.collapsed,
            hidden: this.hidden,
            showHeights: this.showHeights,
            showProperties: this.showProperties,
            showAlignment: this.showAlignment,
        });
    };

    loadSettings = () => {
        const stored: Record<string, any> = JSON.parse(LStorage.getItem('map__station-box') ?? '{}');
        if (stored) {
            this.collapsed = !!stored.collapsed;
            this.hidden = !!stored.hidden;
            this.showHeights = !!stored.showHeights;
            this.showProperties = !!stored.showProperties;
            this.showAlignment = !!stored.showAlignment;
        }
    };

    toggleCollapsed = () => {
        this.collapsed = !this.collapsed;
    };

    onUpdateModel = _.debounce(
        (evt, data) =>
            runInAction(() => {
                this.modelInfo.id = parseInt(data.id);
                this.modelInfo.name = data.name;
                this.modelInfo.props = data.props;
                this.modelInfo.isStructure = data.isStructure;
            }),
        100
    );

    onUpdateMarker = _.debounce(
        () =>
            runInAction(() => {
                if (mapWrapper.clickMarkerLayer.clickMarkerPosition) {
                    const [lon, lat] = olTransform(mapWrapper.clickMarkerLayer.clickMarkerPosition, 'EPSG:3857', 'EPSG:4326');
                    this.lat = lat;
                    this.lon = lon;
                } else {
                    this.lat = 0;
                    this.lon = 0;
                }

                this.modelIds = mapUI.mapPanels?.panels?.models?.getCheckedFileIds() ?? modelsPanelStore.checkedIds.filter((id): id is number => typeof id === 'number');

                if (this.modelInfo.id) {
                    if (!this.modelIds.includes(this.modelInfo.id)) {
                        this.modelInfo = {
                            id: null,
                            name: null,
                            props: null,
                            isStructure: false,
                        };
                    }
                }
            }),
        300
    );

    onRightDrag = (delta: number) => {
        this.showMinimizeOverlay = Math.abs(delta) > 30;
    };

    onRightDragEnd = (delta: number) => {
        if (this.showMinimizeOverlay || Math.abs(delta) < 2) {
            this.hidden = true;
        }
        this.showMinimizeOverlay = false;
    };

    showInfoBox = () => {
        this.hidden = false;
        this.showMinimizeOverlay = false;
        this.onUpdateMarker();
    };

    render() {
        if (this.hidden) {
            const stored = LStorage.getOrDefault<{ x?: number; y?: number }>(`floating-window-map-info-box`, {});
            const top = Math.min(stored.y ?? 200, window.innerHeight - 200);
            return (
                <div id='station-box-hidden-toggle' className='card' style={{ top }} onClick={this.showInfoBox}>
                    <i className='iki iki-compass'></i>
                </div>
            );
        }

        const boundLeft = (document.querySelector('.ol-left-padding') as HTMLDataElement)?.getBoundingClientRect().width;
        return (
            <FloatingWindowSimpleComponent
                onRightDrag={this.onRightDrag}
                onRightDragEnd={this.onRightDragEnd}
                storageId='map-info-box'
                anchor='right'
                defaultX={20}
                defaultY={200}
                boundTop={110}
                boundLeft={boundLeft + 30}
                animate={true}
            >
                <div id='station-stat-box'>
                    {this.showMinimizeOverlay && (
                        <div className='station-stat-box-collapse-overlay'>
                            <i className='iki iki-chevron-up' style={{ fontSize: 32, transform: 'rotate(90deg)' }}></i>
                        </div>
                    )}
                    <header className='flex-column'>
                        <div className='flex-row ai-center gap-third no-select' onClick={this.toggleCollapsed} style={{ cursor: 'pointer', width: '100%' }}>
                            <i className={'iki iki-chevron-down'} style={{ transform: this.collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 250ms' }}></i>

                            <div className='flex-column' style={{ flex: 1, minWidth: 0, alignItems: 'flex-start', paddingRight: '10px' }}>
                                <div className='subtitle'>{__('js_map.info.object_information')}</div>
                                <div className='title' title={this.modelInfo?.name ?? null}>
                                    {this.modelInfo?.name ?? __('js_map.info.object_information.point_on_map')}
                                </div>
                            </div>

                            {/*<i className='iconf-x' style={{ marginLeft: 10 }}></i>*/}
                        </div>

                        {!this.collapsed && false && (
                            <input
                                type='text'
                                placeholder={__('general.search')}
                                value={this.filter}
                                maxLength={30}
                                style={{ padding: '0.5em' }}
                                className='search-input'
                                onChange={e => (this.filter = e.currentTarget.value)}
                            />
                        )}
                    </header>
                    <main className='of-a gap-third' style={{ display: this.collapsed ? 'none' : 'flex', flexDirection: 'column' }}>
                        <CollapsiblePanel
                            label={__('js_logpoints.column_alignment')}
                            defaultIsOpen={true}
                            animated={false}
                            lazyLoad={true}
                            isOpen={this.showAlignment}
                            onToggle={this.toggleShowAlignment}
                        >
                            <MapStationInfo stationInfo={this.stationInfo} />
                        </CollapsiblePanel>

                        {this.modelIds?.length > 0 ? (
                            <CollapsiblePanel
                                label={__('js_map.surface_elevation_and_area')}
                                defaultIsOpen={true}
                                animated={false}
                                lazyLoad={true}
                                isOpen={this.showHeights}
                                onToggle={this.toggleShowHeights}
                                panelStyle={{ marginLeft: -5, marginRight: -5, marginBottom: -4 }}
                            >
                                <ModelsSurfaceHeightAtLocation lat={this.lat} lon={this.lon} modelIds={this.modelIds} />
                            </CollapsiblePanel>
                        ) : null}

                        {!!this.modelInfo?.id && (
                            <CollapsiblePanel
                                label={__('js_map.surface_properties')}
                                defaultIsOpen={true}
                                animated={false}
                                lazyLoad={true}
                                isOpen={this.showProperties}
                                onToggle={this.toggleShowProperties}
                            >
                                <ModelsSurfaceProps modelInfo={this.modelInfo} />
                            </CollapsiblePanel>
                        )}
                    </main>
                </div>
            </FloatingWindowSimpleComponent>
        );
    }

    toggleShowAlignment = state => {
        this.showAlignment = state;
        LStorage.setItem('map__station-box__show-alignment', this.showAlignment);
    };

    toggleShowHeights = state => {
        this.showHeights = state;
        LStorage.setItem('map__station-box__show-heights', this.showHeights);
    };

    toggleShowProperties = state => {
        this.showProperties = state;
        LStorage.setItem('map__station-box__show-props', this.showProperties);
        LStorage.setItem('map__station-box__show-props', this.showProperties);
    };
}
